import { getSnowplowEventPayload, logAnalyticsEvent } from '../analytics';
import { ANALYTICS_CATEGORIES, SNOWPLOW_EVENTS } from '../analytics.const';

const {
	MARKETPLACE: { VEHICLE_LIST },
} = ANALYTICS_CATEGORIES;

export type FilterType = 'multi_select' | 'range' | 'single_select' | 'text';

export type FilterChangeSource = 'chip' | 'filter_bar' | 'filter_panel';

/**
 * Wire contract for filter analytics. Keys are snake_case to match the
 * downstream Snowplow schema, so the camelCase interface-naming rule is
 * disabled here. These types live in the analytics layer so that feature code
 * depends on analytics (not the reverse).
 */
/* eslint-disable local-rules/interfaces-types-properties-naming */
export interface FilterAppliedEvent {
	batch_id: string;
	batch_size: number;
	filter_key?: string;
	filter_name: string;
	filter_raw_values?: string[];
	filter_type: FilterType;
	filter_value?: string;
	filter_value_from?: Nullable<number>;
	filter_value_to?: Nullable<number>;
	filter_values?: string[];
	had_previous_value: boolean;
	source: FilterChangeSource;
}

export interface FilterClearedEvent {
	batch_id: string;
	filter_key?: string;
	filter_name: string;
	filter_type: FilterType;
	source: FilterChangeSource;
}

export interface FilterChipToggleEvent {
	filter_name: string;
}
/* eslint-enable local-rules/interfaces-types-properties-naming */

/**
 * `dlr_filter_applied` — fires once per filter changed in a single apply
 * (modal "Apply" or a filter-bar chip apply). The structured payload is carried
 * in `label` as JSON, matching the existing rich-event convention.
 */
export const triggerFilterAppliedEvent = (event: FilterAppliedEvent): void => {
	const eventPayload = getSnowplowEventPayload({
		category: VEHICLE_LIST,
		customData: { label: JSON.stringify(event) },
		name: 'dlr_filter_applied',
		schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
		version: SNOWPLOW_EVENTS.CLICK.VERSION,
	});

	logAnalyticsEvent({ eventPayload });
};

/**
 * `dlr_filter_cleared` — fires once per filter cleared (modal "Clear all" or a
 * chip's "Clear").
 */
export const triggerFilterClearedEvent = (event: FilterClearedEvent): void => {
	const eventPayload = getSnowplowEventPayload({
		category: VEHICLE_LIST,
		customData: { label: JSON.stringify(event) },
		name: 'dlr_filter_cleared',
		schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
		version: SNOWPLOW_EVENTS.CLICK.VERSION,
	});

	logAnalyticsEvent({ eventPayload });
};

/** `dlr_filter_chip_opened` — a filter-bar chip's dropdown was opened. */
export const triggerFilterChipOpenedEvent = (event: FilterChipToggleEvent): void => {
	const eventPayload = getSnowplowEventPayload({
		category: VEHICLE_LIST,
		customData: { label: JSON.stringify(event) },
		name: 'dlr_filter_chip_opened',
		schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
		version: SNOWPLOW_EVENTS.CLICK.VERSION,
	});

	logAnalyticsEvent({ eventPayload });
};

/** `dlr_filter_chip_closed` — a filter-bar chip's dropdown was closed. */
export const triggerFilterChipClosedEvent = (event: FilterChipToggleEvent): void => {
	const eventPayload = getSnowplowEventPayload({
		category: VEHICLE_LIST,
		customData: { label: JSON.stringify(event) },
		name: 'dlr_filter_chip_closed',
		schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
		version: SNOWPLOW_EVENTS.CLICK.VERSION,
	});

	logAnalyticsEvent({ eventPayload });
};

interface TriggerVrmOrVinSearchEnteredEventProps {
	searchValue: string;
}

/**
 * `dlr_vrm_or_vin_search_entered` — fires once the dealer pauses typing in the VRM/VIN search for 1s.
 */
export const triggerVrmOrVinSearchEnteredEvent = ({ searchValue }: TriggerVrmOrVinSearchEnteredEventProps): void => {
	const eventPayload = getSnowplowEventPayload({
		category: VEHICLE_LIST,
		customData: { label: searchValue },
		name: 'dlr_vrm_or_vin_search_entered',
		schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
		version: SNOWPLOW_EVENTS.CLICK.VERSION,
	});

	logAnalyticsEvent({ eventPayload });
};
